import { useState, useEffect } from 'react';
import { fetchAllBulkEmails } from 'api/emailHistory/bulkEmailsApi';
import { fetchCampaigns } from 'api/emailHistory/campaignsApi';
import { MAX_CAMPAIGNS_FETCH } from 'constants/emailHistory';

/**
 * @hook useBulkEmails
 * @description Fetches bulk emails data with pagination, sorting, and filtering
 * @param {Object} filters - Filter parameters from URL state
 * @returns {Object} Object containing bulk emails data, stats, campaigns list, loading state, and error
 */
export function useBulkEmails(filters) {
  const [bulkEmails, setBulkEmails] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({
    totalBulkSends: 0,
    individualEmailsSent: 0,
    delivered: 0,
    contactsInList: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load campaigns list for dropdown
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const data = await fetchCampaigns({ page: 0, pageSize: MAX_CAMPAIGNS_FETCH });
        setCampaigns(data.rows || []);
      } catch (err) {
        console.error('Error loading campaigns:', err);
        // Don't set error here, just log it
      }
    };
    loadCampaigns();
  }, []);

  useEffect(() => {
    const loadBulkEmails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Convert date range to ISO strings if needed
        let dateFrom = filters.dateFrom;
        let dateTo = filters.dateTo;

        // If dateFrom/dateTo are Date objects, convert to ISO strings
        if (dateFrom instanceof Date) {
          dateFrom = dateFrom.toISOString();
        }
        if (dateTo instanceof Date) {
          dateTo = dateTo.toISOString();
        }

        // Use the new /bulk endpoint which supports optional campaignId filtering
        // This endpoint handles pagination, sorting, and filtering server-side
        const data = await fetchAllBulkEmails({
          campaignId: filters.campaignId || null,
          page: filters.page,
          pageSize: filters.pageSize,
          sortBy: filters.sortBy || 'sendDate',
          sortOrder: filters.sortOrder || 'desc',
          search: filters.search || null,
          dateFrom,
          dateTo,
        });

        const allBulkEmails = data.rows || [];
        const totalBulkSends = data.totalRows || 0;

        setBulkEmails(allBulkEmails);
        setTotalRows(totalBulkSends);

        // Use stats from API response (calculated server-side across all matching records)
        const apiStats = data.stats || {};
        setStats({
          totalBulkSends: apiStats.totalBulkSends ?? totalBulkSends,
          individualEmailsSent: apiStats.individualEmailsSent ?? 0,
          delivered: apiStats.delivered ?? 0,
          contactsInList: apiStats.contactsInList ?? 0,
        });
      } catch (err) {
        console.error('Error fetching bulk emails:', err);
        setError(err.message || 'Failed to load bulk emails');
        setBulkEmails([]);
        setTotalRows(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadBulkEmails();
  }, [
    filters.campaignId,
    filters.page,
    filters.pageSize,
    filters.sortBy,
    filters.sortOrder,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
  ]);

  return {
    bulkEmails,
    totalRows,
    campaigns,
    stats,
    isLoading,
    error,
  };
}

